import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import Header, { ButtonProps } from "go-app/components/Header";
import { MobileActionProps } from "go-app/components/MobileActions/MobileAction";
import MobileActions from "go-app/components/MobileActions/MobileActions";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { useConfirmation } from "go-form/components/ModalConfirm";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";
import { ApplicationTemplateApi } from "../../../../../../../../services/Api/Admin/adminTypes";
import AppForm from "../../components/AppForm";

interface MatchParams {
	appId: string;
}

const AdminAppsEditPage = () => {
	const [app, setApp] = useState<ApplicationTemplateApi | undefined>();
	const { t } = useTranslation("admin");
	const confirmation = useConfirmation();
	const { addSuccessFlash, addFlash } = useFlash();
	const history = useHistory();
	const { appId } = useParams<MatchParams>();
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		fetchApp();
	}, []);

	const fetchApp = async () => {
		try {
			const res = await adminApi.getApp(Number(appId));
			handleChangeTabTitle(
				`${res.provider} | ${t("modules.app.header.edit.title")} | ${t("lib:go_component.navigation.admin")}`
			);
			setApp(res);
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	const removeAppVersion = async () => {
		if (app) {
			try {
				await confirmation({
					title: t("common.word.confirmation", { ns: "lib" }),
					message: t("confirmation.message.action", { ns: "lib" }),
				});
				await adminApi.removeApp(app.id);
				addSuccessFlash(t("lib:common.flash.removed"));
				history.push(`/admin/apps`);
			} catch (e) {
				handleError.alert(e, addFlash);
			}
		}
	};

	if (!app) return <LoadingContainer />;

	const buttons: ButtonProps[] = [
		{
			title: t("common.action.remove", { ns: "lib" }),
			action: removeAppVersion,
			variant: "primary",
		},
	];

	const mobileActions: MobileActionProps[] = [
		{
			title: t("common.action.remove", { ns: "lib" }),
			action: removeAppVersion,
		},
	];

	return (
		<div className="content">
			<MobileActions actions={mobileActions} />
			<Header
				title={`${t("modules.app.header.edit.title")} ${app?.provider}`}
				buttons={isMobile ? [] : buttons}
			/>
			<AppForm app={app} />
		</div>
	);
};

export default AdminAppsEditPage;
